import { createContext, useContext, useId } from "react";
import { cn } from "@/lib/utils";

/**
 * Rótulo + controle + ajuda + erro, com o `id`/`htmlFor` amarrados.
 *
 * Motivo de existir: o app tinha 95 `placeholder=` e ZERO `htmlFor=`. Nenhum
 * campo era anunciado por leitor de tela, e o placeholder desaparece assim que a
 * pessoa digita — levando junto a informação de qual campo é aquele.
 *
 * O id é distribuído por contexto (não por cloneElement), então qualquer controle
 * do kit dentro de um Field se associa sozinho:
 *
 *   <Field label="Nome do produto" required>
 *     <Input value={form.name} onChange={...} />
 *   </Field>
 */
const FieldContext = createContext(null);

export function useFieldContext() {
  return useContext(FieldContext);
}

/**
 * Dois idiomas de rótulo, de propósito:
 * - `eyebrow` (padrão) para o painel, onde o rótulo é etiqueta de dado numa
 *   grade densa e precisa ceder atenção ao valor;
 * - `plain` para loja e autenticação, onde o rótulo é uma frase dirigida à
 *   pessoa e caixa alta soa como formulário de cartório.
 */
const LABEL_TONE = {
  eyebrow: "text-eyebrow uppercase tracking-wider text-slate-500",
  plain: "text-sm font-medium text-slate-700",
};

export default function Field({
  label,
  hint,
  error,
  required = false,
  htmlFor,
  span = false,
  labelTone = "eyebrow",
  className,
  children,
}) {
  const auto = useId();
  const id = htmlFor || auto;
  const hintId = hint ? `${id}-hint` : null;
  const errorId = error ? `${id}-error` : null;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: !!error, required }}>
      <div className={cn(span && "col-span-2", className)}>
        {label && (
          <label htmlFor={id} className={cn("block", LABEL_TONE[labelTone] || LABEL_TONE.eyebrow)}>
            {label}
            {required && <span className="text-red-500 ml-0.5" aria-hidden>*</span>}
            {required && <span className="sr-only"> (obrigatório)</span>}
          </label>
        )}
        <div className={label ? "mt-1.5" : undefined}>{children}</div>
        {hint && !error && (
          <p id={hintId} className="mt-1 text-xs text-slate-400 leading-snug">{hint}</p>
        )}
        {error && (
          <p id={errorId} role="alert" className="mt-1 text-xs font-medium text-red-600 leading-snug">{error}</p>
        )}
      </div>
    </FieldContext.Provider>
  );
}
